import { type ReactNode } from 'react';

import { Table, TableBody, makeStyles } from '@material-ui/core';

import {
  useLabelFilters,
  usePageContentTopOffset,
} from '@spotify/confidence-common-react';
import {
  StickyTableContainerMui as StickyTableContainer,
  type ViewMode,
  useSorting,
} from '@spotify/confidence-core-react';

import {
  IntermediateRenderComponentProps,
  useListWorkflowInstances,
} from '../../hooks/useListWorkflowInstances';
import { WorkflowInstanceExtensionRow } from '../WorkflowInstanceExtensionRow';
import {
  WorkflowInstanceTableHeader,
  headerFields,
} from '../WorkflowInstanceTableHeader';
import { WorkflowInstanceTimeline } from '../WorkflowInstanceTimeline';

const useStyles = makeStyles({
  root: {
    tableLayout: 'fixed',
    '& > tbody > tr > td:not(:first-child)': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& > tbody > tr > td:nth-child(2)': {
      textOverflow: 'clip !important',
    },
  },
});

export const WorkflowInstanceTable = ({
  mode = 'Table',
  topOffset: topOffsetProp,
  workflowId,
  filter,
  emptyStateProps,
  beforeContent,
  showTypeIcons = false,
}: {
  mode?: ViewMode;
  topOffset?: number;
  workflowId?: string;
  filter?: string;
  emptyStateProps?: IntermediateRenderComponentProps['emptyStateProps'];
  beforeContent?: ReactNode;
  showTypeIcons?: boolean;
}) => {
  const contextTopOffset = usePageContentTopOffset();
  const topOffset = topOffsetProp ?? contextTopOffset;
  const classes = useStyles();
  const { tableOrder, sort, isSortingActive, getSortingDirection } = useSorting(
    {
      orderBy: headerFields.created,
      order: 'desc',
    },
  );

  const {
    workflowInstances,
    IntermediateRenderComponent,
    InfiniteScroll,
    InfiniteScrollTableRow,
    showLoading,
  } = useListWorkflowInstances({
    workflowId,
    filter,
    sorting: tableOrder,
  });

  const { labelDefinitions } = useLabelFilters();
  const hasLabels = labelDefinitions.length > 0;

  if (IntermediateRenderComponent) {
    return (
      <>
        {beforeContent}
        <IntermediateRenderComponent
          showLoading={showLoading}
          emptyStateProps={emptyStateProps}
        />
      </>
    );
  }

  if (mode === 'Timeline') {
    return (
      <WorkflowInstanceTimeline
        items={workflowInstances}
        topOffset={topOffset}
        loading={showLoading}
      >
        <InfiniteScroll />
      </WorkflowInstanceTimeline>
    );
  }

  return (
    <StickyTableContainer loading={showLoading}>
      <Table className={classes.root}>
        <WorkflowInstanceTableHeader
          topOffset={topOffset}
          sort={sort}
          isSortingActive={isSortingActive}
          getSortingDirection={getSortingDirection}
        />
        <TableBody>
          {workflowInstances.map(workflowInstance => (
            <WorkflowInstanceExtensionRow
              workflowInstance={workflowInstance}
              key={workflowInstance.name}
              showTypeIcon={showTypeIcons}
            />
          ))}
          <InfiniteScrollTableRow placeholderColumns={hasLabels ? 6 : 5} />
        </TableBody>
      </Table>
    </StickyTableContainer>
  );
};
